// SessionsPanel — shows the user's active auth sessions across all devices,
// with a "Sign out everywhere" button. Phase 0 of the session-control rollout
// (Task #5): visibility only, no enforcement.
//
// Data comes from RPC get_my_active_sessions (RLS-safe wrapper over auth.sessions
// that filters by auth.uid()). The current session's id is read from
// supabase.auth.getSession() so the matching row gets a "this device" badge.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton } from '@shared/ui';
import { NamedIcon, IconRefresh } from '@shared/ui/icons';
import { relativeTime } from '@shared/lib/format';

interface SessionRow {
  session_id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  updated_at: string;
  not_after: string | null;
}

/** Strip CIDR suffix and IPv6-mapped IPv4 prefix from auth.sessions.ip values */
function cleanIp(raw: string | null): string {
  if (!raw) return '—';
  return raw.replace(/\/\d+$/, '').replace(/^::ffff:/, '');
}

/** Pull a short device label out of a user-agent string */
function parseDevice(ua: string | null, t: TFunction): { label: string; icon: string } {
  if (!ua) return { label: t('profile.sessions.unknownDevice', { defaultValue: 'Unknown device' }), icon: 'question' };
  const isMobile = /(iPhone|iPad|Android|Mobile)/i.test(ua);
  const isMac = /Mac OS X/i.test(ua);
  const isWindows = /Windows/i.test(ua);
  const isLinux = /Linux/i.test(ua) && !/Android/i.test(ua);
  const isIphone = /iPhone/i.test(ua);
  const isIpad = /iPad/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isEdge = /Edg\//i.test(ua);
  const isChrome = /Chrome/i.test(ua) && !isEdge;
  const isSafari = /Safari/i.test(ua) && !isChrome && !isEdge;
  const isFirefox = /Firefox/i.test(ua);
  const isElectron = /Electron/i.test(ua);

  let os = 'Unknown OS';
  let icon = 'monitor';
  if (isIphone) { os = 'iPhone'; icon = 'phone'; }
  else if (isIpad) { os = 'iPad'; icon = 'phone'; }
  else if (isAndroid) { os = 'Android'; icon = 'phone'; }
  else if (isMac) { os = 'Mac'; icon = 'monitor'; }
  else if (isWindows) { os = 'Windows'; icon = 'monitor'; }
  else if (isLinux) { os = 'Linux'; icon = 'monitor'; }

  let browser = '';
  if (isElectron) browser = t('profile.sessions.desktopApp', { defaultValue: 'desktop app' });
  else if (isEdge) browser = 'Edge';
  else if (isChrome) browser = 'Chrome';
  else if (isSafari) browser = 'Safari';
  else if (isFirefox) browser = 'Firefox';
  else browser = t('profile.sessions.browserGeneric', { defaultValue: 'browser' });

  return {
    label: `${os}${browser ? ` • ${browser}` : ''}`,
    icon: isMobile ? 'phone' : icon,
  };
}

export default function SessionsPanel() {
  const { t, i18n } = useTranslation('customer');
  const invalidate = useInvalidateSupabase();
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [signingOutAll, setSigningOutAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve the current session's id (so we can highlight "this device")
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      // Supabase Session has no "id" field on its own, but the access_token
      // payload includes a session_id claim. Decode the JWT to extract it.
      const tok = data.session?.access_token;
      if (!tok) return;
      try {
        const payload = JSON.parse(atob(tok.split('.')[1]));
        setCurrentSessionId(payload?.session_id ?? null);
      } catch { /* ignore decode failure — current-device badge won't show */ }
    });
  }, []);

  const { data: sessions = [], isLoading } = useSupabaseQuery<SessionRow[]>(
    ['my-active-sessions'],
    async () => supabase.rpc('get_my_active_sessions'),
  );

  const signOutEverywhere = async () => {
    if (!confirm(t('profile.sessions.confirmGlobalSignout', {
      defaultValue: 'Sign out from all devices? You will be logged out from this device too.',
    }))) return;
    setSigningOutAll(true);
    setError(null);
    try {
      const { error: e } = await supabase.auth.signOut({ scope: 'global' });
      if (e) { setError(e.message); return; }
      // Redirect to login — the global signout invalidated this session too
      window.location.href = '/login';
    } finally {
      setSigningOutAll(false);
    }
  };

  return (
    <div className="c-panel" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-label)', color: 'var(--text)' }}>
          {t('profile.sessions.title', { defaultValue: 'Active sessions' })}
        </h3>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)' }}>
          {sessions.length > 0 && `${sessions.length} ${t('profile.sessions.deviceCount', { count: sessions.length, defaultValue: sessions.length === 1 ? 'device' : 'devices' })}`}
        </span>
      </div>

      <div style={{ fontSize: 'var(--fs-small)', color: 'var(--muted)', marginBottom: 12 }}>
        {t('profile.sessions.description', {
          defaultValue: 'Devices currently signed into your account. If you see a device you don\'t recognize, sign out from all sessions and change your password.',
        })}
      </div>

      {isLoading ? (
        <Skeleton height={120} />
      ) : sessions.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 'var(--fs-body)', padding: 12, textAlign: 'center' }}>
          {t('profile.sessions.empty', { defaultValue: 'No active sessions found.' })}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sessions.map((s) => {
            const isCurrent = s.session_id === currentSessionId;
            const dev = parseDevice(s.user_agent, t);
            return (
              <div
                key={s.session_id}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: isCurrent ? 'rgba(46,204,113,0.06)' : 'rgba(15,45,100,0.15)',
                  border: `1px solid ${isCurrent ? 'rgba(46,204,113,0.4)' : 'rgba(15,45,100,0.4)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}><NamedIcon name={dev.icon} size={22} /></div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {dev.label}
                    {isCurrent && (
                      <span style={{
                        fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-bold)', padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(46,204,113,0.15)', color: '#2ecc71',
                        border: '1px solid rgba(46,204,113,0.4)', letterSpacing: 0.5,
                      }}>
                        {t('profile.sessions.thisDevice', { defaultValue: 'THIS DEVICE' })}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 2 }}>
                    IP: {cleanIp(s.ip)} · {t('profile.sessions.lastActive', { defaultValue: 'last active' })} {relativeTime(s.updated_at, i18n.language)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={signOutEverywhere}
          disabled={signingOutAll || sessions.length === 0}
          style={{
            padding: '8px 14px',
            background: 'transparent',
            color: '#e74c3c',
            border: '1px solid rgba(231,76,60,0.4)',
            borderRadius: 6,
            cursor: signingOutAll || sessions.length === 0 ? 'default' : 'pointer',
            fontFamily: 'inherit',
            fontSize: 'var(--fs-small)',
            fontWeight: 'var(--fw-semibold)',
            opacity: signingOutAll || sessions.length === 0 ? 0.5 : 1,
          }}
        >
          {signingOutAll
            ? t('profile.sessions.signingOut', { defaultValue: 'Signing out everywhere…' })
            : t('profile.sessions.signOutAll', { defaultValue: 'Sign out everywhere' })}
        </button>
        <button
          type="button"
          onClick={() => invalidate(['my-active-sessions'])}
          style={{
            padding: '8px 12px',
            background: 'transparent',
            color: 'var(--muted)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 'var(--fs-caption)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <IconRefresh size={13} /> {t('profile.sessions.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 8, color: '#e74c3c', fontSize: 'var(--fs-small)' }}>{error}</div>
      )}
    </div>
  );
}
